<template>
  <WdsTabs
    v-if="shouldDisplayToggle"
    :active-tab-key="toggledOption"
    @update:active-tab-key="handleToggle"
  >
    <WdsTab :title="$t('Company contacts')" :tab-key="TrustedContactOrigin.COMPANY_CONTACTS" />
    <WdsTab
      v-if="hasOrgs"
      :title="$t('Group contacts')"
      :tab-key="TrustedContactOrigin.ORGANISATION_CONTACTS"
    />
  </WdsTabs>

  <GraphqlDataTable
    :key="forOrganisation ? 'organisation-contacts' : 'company-contacts'"
    v-model:order-by="orderBy"
    v-model:filters="filters"
    v-model:total-count="totalCount"
    :query
    :filter-black-list
    :additional-query-variables
    :max-height-adjustment="30"
    :quick-filters="['statuses', 'countries']"
    :search-placeholder
    :sort-options="trustedContactsSortOptions"
    @row-click="$emit('row-click', $event)"
    @full-screen="$emit('full-screen', $event)"
  >
    <template #action="{ row, index }">
      <TrustedContactActionsColumn
        :row="row"
        :index="index"
        :for-organisation="forOrganisation"
        @edit="$emit('edit', $event)"
        @delete="$emit('delete', $event)"
        @re-invite="$emit('re-invite', $event)"
        @approved="$emit('approved')"
        @approve-error="$emit('approve-error', $event)"
      />
    </template>
    <template #no-results>
      <slot name="no-results">
        {{ $t('No matching records found') }}
      </slot>
    </template>
  </GraphqlDataTable>
</template>

<script lang="ts" setup>
import { computed, provide, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsTabs, WdsTab } from '@worksome/wds-components'
import { TypedQueryDocumentNode } from 'graphql/index'
import { graphql } from '@/gql'
import GraphqlDataTable from '@/components/GraphqlDataTable/GraphqlDataTable.vue'
import TrustedContactActionsColumn from '@/components/GraphqlDataTable/columns/TrustedContacts/TrustedContactActionsColumn.vue'
import { TrustedContactOrigin } from '@/components/Contacts/CompanyFreelancer/types'
import { $t } from '@/composables/translation'
import { ContactStatus } from '@/gql/graphql'
import {
  ContactAccounts,
  DefaultStatuses,
} from '@/components/GraphqlDataTable/filters/trustedContacts/injection'
import {
  trustedContactsSortOptions,
  trustedContactsDefaultOrderBy,
} from '@/components/GraphqlDataTable/columns/TrustedContacts'

const emit = defineEmits<{
  'row-click': [row: any]
  'tab-toggled': [option: TrustedContactOrigin]
  'full-screen': [mode: boolean]
  edit: [row: any]
  delete: [row: any]
  're-invite': [row: any]
  approved: []
  'approve-error': [errors: string[]]
}>()

withDefaults(
  defineProps<{
    searchPlaceholder?: string
  }>(),
  {
    searchPlaceholder: $t('Search name, email, job title...'),
  },
)

const orderBy = ref({ ...trustedContactsDefaultOrderBy })

const toggledOption = ref(TrustedContactOrigin.COMPANY_CONTACTS)

const { result: companyResult } = useQuery(
  graphql(`
    query ContactsGraphqlTableCompanyDivisions($companyId: ID!) {
      company(id: $companyId) {
        id
        organisations(first: 100) {
          paginatorInfo {
            count
          }
          data {
            id
            divisions(first: 100) {
              data {
                id
              }
            }
          }
        }
      }
    }
  `),
  { companyId: currentAccount.globalId },
  { clientId: 'publicApi' },
)

const divisions = computed(() => {
  const orgs = companyResult.value?.company?.organisations?.data ?? []
  return orgs
    .flatMap((org: any) => org.divisions?.data?.map((d: any) => d.id) ?? [])
    .filter((id: string) => id !== currentAccount.globalId)
})

const hasOrgs = computed(
  () => (companyResult.value?.company?.organisations?.paginatorInfo.count ?? 0) > 0,
)

const forOrganisation = computed(
  () => toggledOption.value === TrustedContactOrigin.ORGANISATION_CONTACTS,
)

const shouldDisplayToggle = computed(() => divisions.value.length > 0)

const accounts = computed(() =>
  forOrganisation.value ? divisions.value : [currentAccount.globalId],
)

const defaultStatuses = ref<string[]>([ContactStatus.Active, ContactStatus.Invited])
provide(DefaultStatuses, defaultStatuses)
provide(ContactAccounts, accounts)

const filters = ref<Record<string, unknown>>({
  statuses: [ContactStatus.Active, ContactStatus.Invited],
})

const totalCount = defineModel<number | undefined>('totalCount')

const additionalQueryVariables = computed(() => ({
  companyId: currentAccount.globalId,
  accounts: accounts.value,
}))

const filterBlackList = computed(() => ['accounts', 'companyId', 'states'])

const handleToggle = (optionKey: TrustedContactOrigin) => {
  toggledOption.value = optionKey
  emit('tab-toggled', optionKey)
}

// Company contacts query
const companyContactsQuery = graphql(`
  query TrustedContactsList(
    $accounts: [ID!]
    $page: Int!
    $first: Int!
    $search: String
    $statuses: [ContactStatus!]
    $skills: [ID!]
    $hireHistory: HireHistoryFilterInput
    $hireStatus: HireStatusFilterInput
    $managedStatus: TrustedContactManagedStatus
    $businessSetup: [BusinessEntityType!]
    $invitedByUsers: InvitedByInput
    $origin: TrustedContactOrigin
    $countries: [String!]
    $states: [String!]
    $contactRegions: MarketRegionInput
    $orderBy: [TrustedContactOrderByClauseInput!]
    $companyId: ID!
    $trustedContactCustomFields: [CustomFieldTypeValueInput!]
  ) {
    trustedContacts(
      accounts: $accounts
      page: $page
      first: $first
      search: $search
      statuses: $statuses
      skills: $skills
      origin: $origin
      invitedByUsers: $invitedByUsers
      businessSetup: $businessSetup
      countries: $countries
      states: $states
      contactRegions: $contactRegions
      orderBy: $orderBy
      hireHistory: $hireHistory
      hireStatus: $hireStatus
      managedStatus: $managedStatus
      customFields: $trustedContactCustomFields
    ) {
      data {
        ...TrustedContactWorkerColumn
        ...TrustedContactSkillsColumn
        ...TrustedContactJobsCountColumn
        ...TrustedContactLocationColumn
        ...TrustedContactBusinessSetupColumn
        ...TrustedContactNotesColumn
        ...TrustedContactActionsColumn
      }
    }
  }
`) as TypedQueryDocumentNode

// Organisation contacts query
const organisationContactsQuery = graphql(`
  query OrganisationTrustedContactsList(
    $accounts: [ID!]
    $page: Int!
    $first: Int!
    $search: String
    $statuses: [ContactStatus!]
    $skills: [ID!]
    $hireHistory: HireHistoryFilterInput
    $hireStatus: HireStatusFilterInput
    $managedStatus: TrustedContactManagedStatus
    $businessSetup: [BusinessEntityType!]
    $invitedByUsers: InvitedByInput
    $origin: TrustedContactOrigin
    $countries: [String!]
    $states: [String!]
    $contactRegions: MarketRegionInput
    $orderBy: [TrustedContactOrderByClauseInput!]
    $companyId: ID!
    $trustedContactCustomFields: [CustomFieldTypeValueInput!]
  ) {
    organisationTrustedContacts(
      accounts: $accounts
      page: $page
      first: $first
      search: $search
      statuses: $statuses
      skills: $skills
      origin: $origin
      invitedByUsers: $invitedByUsers
      businessSetup: $businessSetup
      countries: $countries
      states: $states
      contactRegions: $contactRegions
      orderBy: $orderBy
      hireHistory: $hireHistory
      hireStatus: $hireStatus
      managedStatus: $managedStatus
      customFields: $trustedContactCustomFields
    ) {
      data {
        ...TrustedContactWorkerColumn
        ...TrustedContactSkillsColumn
        ...TrustedContactJobsCountColumn
        ...TrustedContactLocationColumn
        ...TrustedContactBusinessSetupColumn
        ...TrustedContactNotesColumn
        ...TrustedContactOriginatingCompanyColumn
        ...TrustedContactActionsColumn
      }
    }
  }
`) as TypedQueryDocumentNode

// Switch query based on toggle state
const query = computed(() =>
  forOrganisation.value ? organisationContactsQuery : companyContactsQuery,
)
</script>

<style scoped lang="scss"></style>
